import PeriodSelection from "@/components/helpers/PeriodSelection";
import { mdiInformationOutline } from "@mdi/js";
import {
	DataType,
	DustData,
	DustDataType,
	OverpressureData,
	SoundData,
	VibrationData,
	VibrationSubType,
	WeatherData,
	WeatherDataType,
	isWithSpectrum,
} from "@soft-db/data-formatter";
import { FieldFactory } from "@soft-db/form";
import { PropType, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import DustDataConfigSettings from "./DustDataConfigSettings";

import { wheneverOnce } from "@/composable/whenever-once";
import { Show } from "@soft-db/soft-utils";
import { SoftIcon, SoftSwitch, SoftTooltip, SoftTypography } from "@soft-db/ui";
import { or } from "@vueuse/math";
import { FieldRule } from "../../../../../packages/form/src/field-rule";
import OverpressureDataConfigSettings from "./OverpressureDataConfigSettings";
import SoundDataConfigSettings from "./SoundDataConfigSettings";
import VibrationDataConfigSettings from "./VibrationDataConfigSettings";
import WeatherDataConfigSettings from "./WeatherDataConfigSettings";

export type DataConfigMode = "timehistory" | "spectrum";

export type QueryDataConfigForm = {
	$dataConfig: (SoundData | VibrationData | WeatherData | DustData | OverpressureData) & {
		period?: number | [number, number][];
	};
};

function isSpectrumFrequency(config: any): boolean {
	return (
		config.spectrum != null &&
		typeof config.spectrum === "string" &&
		config.frequency != null &&
		typeof config.frequency == "number"
	);
}

export default defineComponent({
	name: "QueryDataConfig",
	props: {
		type: String as PropType<DataType>,
		subType: String as PropType<VibrationSubType | WeatherDataType | DustDataType | "">,
		useField: {
			type: Function as PropType<FieldFactory<QueryDataConfigForm>>,
			required: true,
		},
		mode: {
			type: String as PropType<DataConfigMode>,
			default: "timehistory",
		},
		showPeriod: Boolean,
		periodRules: Object as PropType<FieldRule<QueryDataConfigForm>>,
		allowIntervals: Boolean,
	},
	setup(props) {
		const isSpectrumMode = computed(() => props.mode === "spectrum");

		const spectrumFrequencyMode = ref(false);
		const dataConfig = props.useField("$dataConfig");

		const supportFrequencyMode = computed(() => {
			return props.type === "sound" || props.type === "vibration";
		});

		const allowSpectrumFrequency = computed(
			() => props.mode === "timehistory" && supportFrequencyMode.value
		);

		wheneverOnce(
			dataConfig.data,
			(config) => {
				if (isSpectrumFrequency(config) && allowSpectrumFrequency.value) {
					spectrumFrequencyMode.value = true;
				}
			},
			{ immediate: true }
		);

		watch(spectrumFrequencyMode, () => {
			const def = dataConfig.data.value;
			if (def == null) return;

			if (isWithSpectrum(def)) {
				def.spectrum = undefined as any;
				def.frequency = undefined;
			}
		});

		const showSpectrum = or(isSpectrumMode, spectrumFrequencyMode);

		function getComponent(type: DataType) {
			switch (type) {
				case "sound":
					return (
						<SoundDataConfigSettings
							useField={props.useField}
							showSpectrum={showSpectrum.value}
							showFrequency={spectrumFrequencyMode.value}
						/>
					);
				case "vibration":
					return (
						props.subType && (
							<VibrationDataConfigSettings
								useField={props.useField}
								subType={props.subType as VibrationSubType}
								showSpectrum={showSpectrum.value}
								showFrequency={spectrumFrequencyMode.value}
							/>
						)
					);
				case "weather":
					return (
						props.subType && (
							<WeatherDataConfigSettings
								useField={props.useField}
								subType={props.subType as WeatherDataType}
							/>
						)
					);
				case "dust":
					return <DustDataConfigSettings useField={props.useField} />;
				case "overpressure":
					return <OverpressureDataConfigSettings useField={props.useField} />;
				default:
					return null;
			}
		}

		const { t } = useI18n();
		const period = props.useField("$dataConfig.period", props.periodRules);

		return () => (
			<div class="flex flex-col gap-2">
				<Show when={allowSpectrumFrequency.value}>
					<div class="flex items-center justify-end gap-2 pt-2">
						<SoftTypography tag="h4">{t("data.spectrumFrequencyHistory")}</SoftTypography>
						<SoftSwitch v-model={spectrumFrequencyMode.value} />
						<SoftTooltip tooltip={t("data.spectrumFrequencyHistoryHelp")}>
							<SoftIcon>{mdiInformationOutline}</SoftIcon>
						</SoftTooltip>
					</div>
				</Show>
				<div class="auto-grid gap-2">
					{props.type && getComponent(props.type)}
					{props.showPeriod && (
						<PeriodSelection
							v-model={period.data.value}
							label={t("common.period")}
							allowIntervals={props.allowIntervals}
							errors={period.errors.value}
						/>
					)}
				</div>
			</div>
		);
	},
});
